import React, { useRef } from "react";
import { Camera, X } from "lucide-react";

/**
 * Conversion image → base64 redimensionnée (max 1200px, qualité 0.75)
 * pour limiter le poids stocké en base.
 */
async function fileToCompressedBase64(file, maxSize = 1200, quality = 0.75) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          if (width >= height) {
            height = Math.round(height * (maxSize / width));
            width = maxSize;
          } else {
            width = Math.round(width * (maxSize / height));
            height = maxSize;
          }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function PhotoCapture({ photos, onChange, max = 6 }) {
  const inputRef = useRef(null);

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    const remaining = max - photos.length;
    const accepted = list.slice(0, remaining);
    const converted = [];
    for (const f of accepted) {
      try {
        const b64 = await fileToCompressedBase64(f);
        converted.push(b64);
      } catch (err) {
        console.debug("photo compression failed:", err?.message);
      }
    }
    onChange([...photos, ...converted]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (i) => {
    onChange(photos.filter((_, idx) => idx !== i));
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        onChange={(e) => handleFiles(e.target.files)}
        className="hidden"
        data-testid="photo-input"
      />

      <div className="flex flex-wrap gap-2">
        {photos.map((p, i) => {
          // base64 ≠ entre photos → on prend une tranche suffisamment unique comme key stable
          const key = p.slice(-48);
          return (
            <div
              key={key}
              data-testid={`photo-${i}`}
              className="relative w-20 h-20 rounded-2xl overflow-hidden bg-stone-100 border border-stone-200"
            >
              <img src={p} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => remove(i)}
                data-testid={`photo-remove-${i}`}
                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-rose-500 hover:bg-rose-600 text-white grid place-items-center"
              >
                <X className="w-3 h-3" strokeWidth={3} />
              </button>
            </div>
          );
        })}
        {photos.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid="photo-add"
            className="w-20 h-20 rounded-2xl border-2 border-dashed border-stone-300 hover:border-emerald-400 hover:bg-emerald-50 grid place-items-center text-stone-500 hover:text-emerald-600 transition-all active:scale-95"
          >
            <Camera className="w-6 h-6" strokeWidth={2.5} />
          </button>
        )}
      </div>
      <p className="text-xs text-stone-500 mt-2">
        {photos.length}/{max} photos · {"ouvre l'appareil photo sur mobile"}
      </p>
    </div>
  );
}
